import camelCase from 'lodash/camelCase';
import isNil from 'lodash/isNil';

import { airTankTypes, compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { useTranslation } from 'react-i18next';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';

const compressors = compressorTypes.filter(type => type !== DeviceType.Blower);

export const getInverterSpeedValue = (entity, tvalue) => {
  let variableSpeed;
  switch (camelCase(entity.deviceType)) {
    case camelCase(DeviceType.VariableSpeedDrive):
    case camelCase(DeviceType.VariableSpeedDriveAndPowerSensor):
      if (
        (isNil(entity.inverterSpeedRPM) && isNil(entity.estimatedInverterSpeedRPM)) ||
        isNil(entity.maxInverterSpeedRPM) ||
        entity.maxInverterSpeedRPM === 0
      )
        return undefined;

      variableSpeed =
        ((entity.estimatedInverterSpeedRPM ? entity.estimatedInverterSpeedRPM : entity.inverterSpeedRPM) /
          entity.maxInverterSpeedRPM) *
        100;
      return tvalue(variableSpeed, 'rpm');

    default:
      return entity.inverterSpeedRPM;
  }
};

export const getInverterSpeedUnit = (entity, t) => {
  switch (camelCase(entity.deviceType)) {
    case camelCase(DeviceType.VariableSpeedDrive):
    case camelCase(DeviceType.VariableSpeedDriveAndPowerSensor):
      return t('%');

    default:
      return t('RPM');
  }
};

export const getInverterSpeedTooltipContent = (entity, tvalue, t) => {
  switch (camelCase(entity.deviceType)) {
    case camelCase(DeviceType.VariableSpeedDrive):
    case camelCase(DeviceType.VariableSpeedDriveAndPowerSensor):
      if (!getInverterSpeedValue(entity, tvalue)) return null;
      return entity.estimatedInverterSpeedRPM
        ? t('kW based Calculation')
        : `${tvalue(entity.inverterSpeedRPM, 'rpm')} ${t('RPM')} / ${tvalue(entity.maxInverterSpeedRPM, 'rpm')} ${t(
            'RPM',
          )}`;

    default:
      return null;
  }
};

export const useLabelValueItemsByDeviceType = entity => {
  const {
    modelDisplayName,
    platform,
    coolingType,
    deviceType,
    capacity,
    cycleMinutes,
    deviceId,
    lastHourDuty,
    dryerOperationMode,
  } = entity;
  const { t } = useTranslation();
  const { tvalue, tunit } = useMeasurement();
  const { devicesStore } = useStores();

  const labelValueItems = [];

  if (compressors.includes(deviceType)) {
    const serialNumber = devicesStore.getDeviceAttributeField(deviceId, 'serialNumber') ?? t('N/A');

    labelValueItems.push(
      { label: t('serialNumber'), value: serialNumber },
      { label: t('model'), value: modelDisplayName },
      ...(platform ? [{ label: t('platform'), value: t(platform) }] : []),
      ...(coolingType ? [{ label: t('coolingType'), value: t(coolingType) }] : []),
      { label: t('compressorType'), value: t(deviceType) },
    );
  } else if (airTankTypes.includes(deviceType as DeviceType)) {
    labelValueItems.push({ label: t('capacity'), value: tvalue(capacity, 'liter'), unit: tunit('Liter') });
  } else if (deviceType === DeviceType.Booster) {
    labelValueItems.push(
      { label: t('cycle'), value: tvalue(cycleMinutes, 'minute'), unit: t('Minutes') },
      { label: t('duty'), value: tvalue(lastHourDuty, 'percentage'), unit: t('%') },
    );
  } else if (deviceType === DeviceType.DesiccantAirDryer) {
    labelValueItems.push({ label: t('operation mode'), value: t(dryerOperationMode) });
  }

  return labelValueItems;
};
